import { getEdgeChallengeUrl, getEdgeSignUrl } from "./edge";
import { uploadImageToS3 } from "../helpers";

// ── Crypto helpers ──

export async function computeSHA256(data) {
  const buffer =
    data instanceof ArrayBuffer ? data
    : ArrayBuffer.isView(data) ? data.buffer
    : await data.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ── Challenge endpoint ──
// Server no longer returns server_seed — only challenge_id and timing

export async function requestCaptureChallenge(requestId) {
  const url = getEdgeChallengeUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ request_id: requestId }),
  });
  if (!res.ok) throw new Error(`Challenge request failed: ${res.status}`);
  return res.json();
}

// ── Device signals ──

export function collectDeviceSignals() {
  const canvas = document.createElement("canvas");
  let gpuRenderer = "unknown";
  try {
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (gl) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      if (dbg) gpuRenderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
    }
  } catch (_) {
    // GPU detection not available
  }

  return {
    platform: navigator.platform || "unknown",
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenRes: `${screen.width}x${screen.height}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    gpuRenderer,
    touchPoints: navigator.maxTouchPoints || 0,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// ── Runtime integrity checks ──
// Detect if key browser APIs have been monkey-patched (console injection / proxy)

// eslint-disable-next-line no-empty
function checkRuntimeIntegrity() {
  const flags = [];
  try {
    if (typeof crypto !== "undefined" && crypto.subtle && crypto.subtle.digest) {
      const s = Function.prototype.toString.call(crypto.subtle.digest);
      if (!/\[native code\]/.test(s)) flags.push("CRYPTO_DIGEST_MODIFIED");
    }
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(window.fetch);
    if (!/\[native code\]/.test(s)) flags.push("FETCH_MODIFIED");
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(XMLHttpRequest.prototype.open);
    if (!/\[native code\]/.test(s)) flags.push("XHR_MODIFIED");
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(HTMLCanvasElement.prototype.toDataURL);
    if (!/\[native code\]/.test(s)) flags.push("CANVAS_TODATAURL_MODIFIED");
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(HTMLCanvasElement.prototype.toBlob);
    if (!/\[native code\]/.test(s)) flags.push("CANVAS_TOBLOB_MODIFIED");
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(performance.now);
    if (!/\[native code\]/.test(s)) flags.push("PERF_NOW_MODIFIED");
  } catch (_) { /* API may not exist */ }

  try {
    const s = Function.prototype.toString.call(Date.now);
    if (!/\[native code\]/.test(s)) flags.push("DATE_NOW_MODIFIED");
  } catch (_) { /* API may not exist */ }

  return flags;
}

// ── Trap field generators ──
// These produce values the server validates. An attacker must reverse-engineer
// each one from minified code. Wrong values burn the challenge and get logged.

function computeTrapFields(challengeId, suffix, frameChain, capturedAt) {
  // _seq: XOR of frame chain length and last 2 digits of captured_at
  const _seq = (frameChain.length ^ (capturedAt % 100)) & 0xFF;

  // _ck: simple checksum of challenge_id chars + suffix length
  let ckSum = suffix.length;
  for (let i = 0; i < challengeId.length; i++) {
    ckSum = ((ckSum * 31) + challengeId.charCodeAt(i)) & 0xFFFF;
  }
  const _ck = ckSum.toString(16).padStart(4, "0");

  // _ref: sum of all gate values in frame chain, mod 256, as 2-char hex
  let gSum = 0;
  for (let i = 0; i < frameChain.length; i++) {
    gSum += frameChain[i].g || 0;
  }
  const _ref = (gSum & 0xFF).toString(16).padStart(2, "0");

  // _ts2: bitwise rotation of session relationship
  const _ts2 = ((capturedAt >>> 8) ^ (frameChain.length * 7)) & 0xFFF;

  return { _seq, _ck, _ref, _ts2 };
}

// ── Visibility tracking ──
// Records tab visibility changes during the session — switching to another tab
// to write an injection script produces a distinctive pattern

export function createVisibilityTracker() {
  const changes = [];
  const startTime = performance.now();

  const handler = () => {
    changes.push({
      t: Math.round(performance.now() - startTime),
      v: document.visibilityState === "visible" ? 1 : 0,
    });
  };

  document.addEventListener("visibilitychange", handler);

  return {
    getChanges: () => changes.slice(-20),
    destroy: () => document.removeEventListener("visibilitychange", handler),
  };
}

// ── Touch telemetry collector ──
// Records touch interactions on the capture overlay — console scripts produce
// zero touch events, which is an obvious signal

export function createTouchTracker(element) {
  const events = [];
  const startTime = performance.now();

  const handler = (e) => {
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      events.push({
        t: Math.round(performance.now() - startTime),
        x: Math.round(touch.clientX),
        y: Math.round(touch.clientY),
        r: Math.round((touch.radiusX || 0) * 10) / 10,
        f: Math.round((touch.force || 0) * 100) / 100,
      });
    }
  };

  // Listen on the document body to catch all touches during capture
  const target = element || document.body;
  target.addEventListener("touchstart", handler, { passive: true });
  target.addEventListener("touchmove", handler, { passive: true });

  return {
    getEvents: () => events.slice(-30),
    destroy: () => {
      target.removeEventListener("touchstart", handler);
      target.removeEventListener("touchmove", handler);
    },
  };
}

// ── Motion telemetry collector ──
// Records accelerometer/gyroscope readings — a phone being held has
// characteristic micro-movements that are expensive to synthesize

export function createMotionTracker() {
  const samples = [];
  const startTime = performance.now();

  const handler = (e) => {
    const a = e.accelerationIncludingGravity;
    if (!a) return;
    samples.push({
      t: Math.round(performance.now() - startTime),
      ax: Math.round((a.x || 0) * 100) / 100,
      ay: Math.round((a.y || 0) * 100) / 100,
      az: Math.round((a.z || 0) * 100) / 100,
    });
  };

  window.addEventListener("devicemotion", handler);

  return {
    getSamples: () => samples.slice(-20),
    destroy: () => window.removeEventListener("devicemotion", handler),
  };
}

// ── Camera signal collection ──
// Reads the active video track to detect virtual cameras. Real phone cameras
// report facingMode "user" or "environment"; virtual cameras (OBS, ManyCam)
// typically return undefined or empty because they have no physical direction.

export function collectCameraSignals(videoElement) {
  try {
    const stream = videoElement?.srcObject;
    if (!stream) return null;
    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    const settings = track.getSettings();
    const label = track.label || "";

    let supportedFacingModes = [];
    if (typeof track.getCapabilities === "function") {
      try {
        const caps = track.getCapabilities();
        supportedFacingModes = caps?.facingMode || [];
      } catch (_) { /* capabilities may not be supported */ }
    }

    return {
      facingMode: settings.facingMode || null,
      label,
      width: settings.width,
      height: settings.height,
      supportedFacingModes,
    };
  } catch (_) {
    return null;
  }
}

// ── Server-side signing ──
// Instead of signing locally, we send the unsigned envelope data to the server.
// The server holds the signing key, validates trap fields, and returns an
// envelope_id. The frontend never sees the server_seed.

async function requestEnvelopeSign(payload) {
  const url = getEdgeSignUrl();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Signing request failed: ${res.status} ${body}`);
  }
  return res.json();
}

// ── Top-level convenience function ──

export async function processAndUploadWithIntegrity(
  imageKey,
  imageBlob,
  challenge,
  requestId,
  suffix,
  frameChain,
  sessionTimestamps,
  settings,
  telemetry
) {
  // If no challenge available, fall back to plain upload
  if (!challenge) {
    console.warn("[CaptureIntegrity] No challenge available — plain upload");
    return uploadImageToS3(imageKey, imageBlob, settings);
  }

  try {
    const imageBytes = await imageBlob.arrayBuffer();
    const imageHash = await computeSHA256(imageBytes);
    const deviceInfo = collectDeviceSignals();
    const runtimeFlags = checkRuntimeIntegrity();
    const capturedAt = Date.now();
    const chain = (frameChain || []).slice(-30);
    const trapFields = computeTrapFields(
      challenge.challenge_id, suffix, chain, capturedAt
    );

    // Build the signing payload — everything the server needs to validate and sign
    const signingPayload = {
      version: 2,
      challenge_id: challenge.challenge_id,
      request_id: requestId,
      image_hash: imageHash,
      suffix,
      frame_chain: chain,
      device_info: deviceInfo,
      session_timestamps: sessionTimestamps || {},
      captured_at: capturedAt,
      runtime_flags: runtimeFlags,
      interaction: {
        touch: telemetry?.touch?.getEvents?.() || [],
        motion: telemetry?.motion?.getSamples?.() || [],
        visibility: telemetry?.visibility?.getChanges?.() || [],
        camera: telemetry?.camera || null,
      },
      ...trapFields,
    };

    // Server validates, signs, stores envelope, returns envelope_id
    const { envelope_id } = await requestEnvelopeSign(signingPayload);

    // Upload image to S3 with only the envelope_id reference
    // Server correlates the S3 object with the stored envelope
    const metadata = {
      "integrity-envelope-id": envelope_id,
      "integrity-version": "2",
    };

    return uploadImageToS3(
      imageKey,
      imageBlob,
      settings,
      "image/jpeg",
      metadata
    );
  } catch (err) {
    console.warn("[CaptureIntegrity] Integrity flow failed, falling back to plain upload:", err);
    return uploadImageToS3(imageKey, imageBlob, settings);
  }
}
